import { observer } from 'mobx-react-lite';
import FilterForm from './components/FilterForm';
import TableCustomOnlyTwoDept from '@/components/table/custom/TableCustomOnlyTwoDept';
import { cn, scrollToTop } from '@/utils/utils';
import { formatCurrency } from '@/utils/formatNumber';
import {
    DEFAULT_SELECT_PAGE_SIZES,
    DISPPLAY_MOMENT_YM,
    DISPPLAY_MOMENT_YMD_HM,
    ekDomain,
    ROUTES
} from '@/configs/constants';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '@/hooks/useStore';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import TableActionButtons from '@/components/common/TableActionButtons';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ROLE_USER } from '@/types/enums';
import { flowResult } from 'mobx';
import { getMonth, getYear } from 'date-fns';
import yup from '@/services/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatDateTime } from '@/utils/datetime';
import Tooltip from '@/components/common/Tooltip';
import { Button, ButtonCusWithIcon } from '@/components/common/Button';
import U20Icon from '@/assets/icons/u20-icon.png';
import { Commission } from '@/types/commission';
import { isPathEk } from '@/utils/routes';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { ListFilter, Search } from 'lucide-react';
import IssueInvoiceForm from '../commission-owner/components/IssueInvoiceForm';
import { CommissionMonthYearOwner } from '@/types/http-payload/commission';
import Combobox from '@/components/common/Combobox';

export default observer(function CommissionManagementPage() {
    // hook
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const isEk = isPathEk(pathname);

    // store
    const {
        locationStore: { getLocations },
        accountStore: { getAccounts },
        generalStore: { myAccount },
        bikeStore: { bikeModelList, getListModelName },
        commissionStore: {
            commissions,
            totalRecord,
            paging,
            keepSearchParams,
            total,
            totalRevenueCommission,
            totalCommission,
            getCommissions,
            exportFileCommissionExcel,
            exportFileCommissionPdf,
            setObservable
        },
        modalStore: { showModal, showAlertModal, hideAllModals },
        uiStore: { devicesScreen: { desktop } }
    } = useStore();

    // state
    const [rowSelected, setRowSelected] = useState({});
    // const
    const currentYear = getYear(new Date());
    const currentMonth = getMonth(new Date()) + 1;

    // form
    const validateSchema = yup.object().shape({
        ownerId: yup.string(),
        locationId: yup.string(),
        year: yup.string(),
        month: yup.string(),
        bikeModel: yup.string().trim(),
        keyword: yup.string().trim()
        // isCancelBooking: yup.boolean().default(false).optional()
    });
    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            ownerId: keepSearchParams?.ownerId?.toString() ?? '',
            locationId: keepSearchParams?.locationId?.toString() ?? '',
            year: keepSearchParams?.year?.toString() ?? '',
            month: keepSearchParams?.month?.toString() ?? '',
            bikeModel: keepSearchParams?.bikeModel ?? '',
            keyword: keepSearchParams?.keyword ?? ''
        }
    });

    // lifecycle
    useEffect(() => {
        // fetch data form filter
        getLocations({ page: -1, size: 10 });
        getListModelName();

        // fetch commissions
        onFetchData();
    }, []);

    // function
    const isOwner = useMemo((): boolean => {
        if (!myAccount?.role?.id) return false;

        if (
            myAccount?.role?.id === ROLE_USER.FRANCHISE_OWNER_PRM ||
            myAccount?.role?.id === ROLE_USER.FRANCHISE_OWNER_STD ||
            myAccount?.role?.id === ROLE_USER.HOTEL_BIKE_OWNER ||
            myAccount?.role?.id === ROLE_USER.SHARE_BIKE_OWNER
        ) {
            return true;
        }

        return false;
    }, [myAccount?.role?.id]);

    const isAdmin = useMemo((): boolean => {
        if (!myAccount?.role?.id) return false;

        if (myAccount?.role?.id === ROLE_USER.BEST_BIKE_ADMIN) {
            return true;
        }

        return false;
    }, [myAccount?.role?.id]);

    const onFetchData = async () => {
        if (!isOwner) {
            getAccounts({ isOwner: true, page: -1, size: 10 });
        }

        await flowResult(
            getCommissions(
                keepSearchParams || {
                    year: currentYear,
                    month: currentMonth
                }
            )
        );

        if (!keepSearchParams) {
            setObservable(
                'keepSearchParams',
                { year: currentYear, month: currentMonth },
                { isMergeObject: false }
            );

            form.setValue('year', currentYear.toString());
            form.setValue('month', currentMonth.toString());
        }
    };

    // columns
    const columns: ColumnDef<Commission>[] = [
        {
            id: 'id',
            accessorKey: 'id',
            header: '',
            size: 80,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <TableActionButtons
                    onView={() => handleClickView(original.orderId)}
                />
            )
        },

        {
            accessorKey: 'commissionClosingTime',
            header: t('field.commission_recording_month'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original?.commissionClosingTime || '', DISPPLAY_MOMENT_YM, { useLocalTime: false }) || ''}
                </div>
            )
        },
        {
            accessorKey: 'rentalFrom',
            header: t('field.reservation.rental_time'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original?.rentalFrom || '', DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) || ''}
                </div>
            )
        },
        {
            accessorKey: 'rentalTo',
            header: t('field.reservation.return_time'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalTo || '', DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) || ''}
                </div>
            )
        },
        {
            accessorKey: 'location',
            header: t('field.location_name'),
            size: 200,
            meta: {
                headerClassName: 'min-w-[60px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>{original?.location}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'bikeModel',
            header: t('field.model_bike'),
            size: 250,
            meta: {
                headerClassName: 'min-w-[80px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[120px] line-clamp-2'>{original?.bikeModel}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'renterName',
            // header: t('field.renter_name'),
            header: t('field.customer_name'),
            size: 260,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[90px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='flex justify-start gap-1 min-w-[140px] break-all'>
                    {original?.u20 && (
                        <Tooltip triggerClassName='min-w-6' content={t('sentences.under_20_age')}>
                            <img
                                src={U20Icon}
                                alt={`(${t('sentences.under_20_age')})`}
                                className='size-6'
                            />
                        </Tooltip>
                    )}
                    <p className='text-left break-all line-clamp-2'>
                        {original?.renterName || ''}
                    </p>
                </div>
            )
        },
        {
            accessorKey: 'insurance',
            header: t('field.vehicle_compensation'),
            size: 260,
            meta: {
                headerClassName: 'center-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-center'>{original?.insurance}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'owner.fullname',
            header: t('field.owner'),
            // header: '所有者',
            size: 100,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[80px] line-clamp-2'>{original?.owner?.fullname ?? ''}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'owner.code',
            header: t('field.user_code'),
            size: 100,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap min-w-[80px] line-clamp-2'>{original?.owner?.code ?? ''}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'paymentType',
            header: t('field.payment_method'),
            size: 100,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap min-w-[100px] line-clamp-2'>{original?.paymentType ?? ''}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'totalPayment',
            header: t('words_title.revenue'),
            enableSorting: false,
            size: 300,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-right'>
                    {t('currency.currency_symbol')}
                    {formatCurrency(original.totalPayment) ?? '0'}
                </div>
            )
        },
        {
            accessorKey: 'commissionRevenueAmount',
            header: t('field.revenue_calculation_amount'),
            enableSorting: false,
            size: 300,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-right'>
                    {t('currency.currency_symbol')}
                    {formatCurrency(original.commissionRevenueAmount) ?? '0'}
                </div>
            )
        },
        {
            accessorKey: 'commissionAmount',
            header: t('field.commisison_rate'),
            enableSorting: false,
            size: 300,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-right'>
                    {t('currency.currency_symbol')}
                    {formatCurrency(original.commissionAmount) ?? '0'}
                </div>
            )
        },
        {
            accessorKey: 'orderCodeSystem',
            header: t('field.reservation_code'),
            size: 260,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[90px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div>{original?.orderCodeSystem || ''}</div>
            )
        }
    ];

    const onSearch = (data: any) => {
        const dataSearch = { ...data };

        if (isOwner) {
            dataSearch.isOwner = myAccount?.id || null;
        }

        getCommissions(dataSearch, {
            ...paging,
            pagination: { ...paging.pagination, pageIndex: 0 }
        });

        setObservable('keepSearchParams', dataSearch, { isMergeObject: false });
        hideAllModals();
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getCommissions(keepSearchParams, newPaging);
        scrollToTop();
    };

    const handleClickView = (id) => {
        if (isEk) {
            navigate(
                `/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`
            );
        } else {
            navigate(
                `${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`
            );
        }
    };

    const onClickExportExcel = async () => {
        const res = await flowResult(exportFileCommissionExcel({ ...keepSearchParams }));
        if (res) {
            showAlertModal({
                id: 'export-success',
                type: 'success',
                content: t('messages.export_file_success')
            });
        }
    };

    const onClickExportPdf = async () => {
        const res = await flowResult(exportFileCommissionPdf({ ...keepSearchParams }));
        if (res) {
            showAlertModal({
                id: 'export-success',
                type: 'success',
                content: t('messages.export_file_success')
            });
        }
    };

    const onClickMonthlyInvoicing = (): string => {
        return isEk ?
            `${ROUTES.commissionOwnerMontly.hrefEK}` :
            `${ROUTES.commissionOwnerMontly.href}`;
    };

    const handleOpenFilter = () => {
        showModal({
            id: 'filter',
            size: 'lg',
            title: t('words_title.filter'),
            titleClassName: 'text-center w-full',
            footerClassName: 'hidden p-0 m-0',
            content: <FilterForm form={form} isOwner={isOwner} isInModal={true} onSearch={onSearch} />
        });
    };

    const onIssueInvoice = () => {
        const idModal = 'owwer-issue-invoice-modal';
        let item: CommissionMonthYearOwner = {
            ownerId: myAccount?.id,
            commissionYear: keepSearchParams?.year ?? '',
            commissionMonth: keepSearchParams?.month ?? ''
        };

        showAlertModal({
            id: idModal,
            title: t('words_title.rental_bike_fee_table_issued'),
            size: '2xl',
            titleClassName: 'text-center max-w-none',
            content: (
                <IssueInvoiceForm idModal={idModal} adjust={item} isAdmin={isAdmin} />
            ),
            footerClassName: 'hidden'
        });
    };

    const renderButtonTable = () => {
        return (
            <div className={cn(
                isOwner ?
                    'grid w-full grid-cols-2 md:flex md:justify-end md:flex-wrap gap-4' :
                    'grid w-full grid-cols-2 sm:grid-cols-3 md:flex md:justify-end md:flex-wrap gap-4'
            )}
            >
                {isAdmin && (
                    <Link to={onClickMonthlyInvoicing()} className='col-span-full sm:col-span-1'>
                        <ButtonCusWithIcon
                            variant='orange3'
                            size='md'
                            className='h-9.5 w-full md:w-52 md:h-12'
                            type='button'
                            iconId='#icon-print'
                            widthIcon={19}
                            heightIcon={20}
                        >
                            {t('button.monthly_invoicing')}
                        </ButtonCusWithIcon>
                    </Link>
                )}
                {isOwner && (
                    <ButtonCusWithIcon
                        variant='orange3'
                        size='md'
                        className='h-9.5 w-full md:w-52 md:h-12 col-span-full sm:col-span-1'
                        type='button'
                        iconId='#icon-print'
                        widthIcon={19}
                        heightIcon={20}
                        onClick={() => onIssueInvoice()}
                    >
                        {t('button.monthly_invoicing')}
                    </ButtonCusWithIcon>
                )}
                <ButtonCusWithIcon
                    variant='default'
                    size='md'
                    className='h-9.5 w-full md:w-52 md:h-12'
                    onClick={() => onClickExportPdf()}
                    iconId='#icon-pdf'
                    widthIcon={16}
                    heightIcon={18}
                >
                    {t('button.export_to_pdf')}
                </ButtonCusWithIcon>
                <ButtonCusWithIcon
                    variant='green'
                    size='md'
                    className='h-9.5 w-full md:w-52 md:h-12'
                    onClick={() => onClickExportExcel()}
                    iconId='#icon-excel'
                    widthIcon={20}
                    heightIcon={20}
                >
                    {t('button.export_to_excel')}
                </ButtonCusWithIcon>
            </div>
        );
    };

    const renderTotalComponent = useMemo((): ReactNode => {
        return (
            <table className='border-none text-right'>
                <tbody className='text-title-table'>
                    <tr>
                        <th className='pb-1.5'>
                            <div className='inline-block px-2 py-1 border-[2px] border-title-table font-semibold rounded-[8px]'>
                                TOTAL
                            </div>
                        </th>
                        <td className='pl-5 pb-1.5'>
                            <div className='text-fs-mid18 text-title-table font-semibold'>
                                {t('currency.currency_symbol')}
                                {formatCurrency(total) ?? '0'}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th className='font-normal pb-1.5'>{t('field.revenue_calculation_amount')}</th>
                        <td className='font-semibold pl-5 pb-1.5'>
                            {t('currency.currency_symbol')}
                            {formatCurrency(totalRevenueCommission) ?? '0'}
                        </td>
                    </tr>
                    <tr>
                        <th className='font-normal'>{t('field.commisison_rate')}</th>
                        <td className='font-semibold pl-5'>
                            {t('currency.currency_symbol')}
                            {formatCurrency(totalCommission) ?? '0'}
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    }, [total, totalRevenueCommission, totalCommission]);

    return (
        <>
            {
                desktop ?
                    (
                        <div className='layout-card'>
                            <FilterForm form={form} isOwner={isOwner} onSearch={onSearch} />
                        </div>
                    ) :
                    (
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSearch)}>
                                <div className='flex justify-between gap-2.5 relative'>
                                    {/* <FormField
                                        control={form.control}
                                        name='keyword'
                                        render={({ field }) => (
                                            <FormItem className='flex-1 relative'>
                                                <FormControl>
                                                    <Input
                                                        className='h-10 pl-10'
                                                        type='text'
                                                        placeholder={t('field.reservation_code_renter_name')}
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <button className='-translate-y-1/2 absolute left-3 size-5 top-1/2'>
                                                    <Search />
                                                </button>
                                            </FormItem>
                                        )}
                                    /> */}

                                    <FormField
                                        control={form.control}
                                        name='bikeModel'
                                        render={({ field }) => (
                                            <FormItem className='flex-1 relative'>
                                                <FormControl>
                                                    <Combobox
                                                        placeholder={t('field.model_name')}
                                                        value={field.value ?? ''}
                                                        onChange={(item) => {
                                                            field.onChange(item?.code ?? '');
                                                        }}
                                                        search={true}
                                                        items={
                                                            (Array.isArray(bikeModelList) && bikeModelList?.length > 0 ?
                                                                [
                                                                    ...bikeModelList.map(item => ({
                                                                        label: item.bikeModel,
                                                                        value: item.bikeModel?.toString(),
                                                                        code: item.bikeModel
                                                                    }))
                                                                ] :
                                                                []
                                                            )
                                                        }
                                                        className='h-10 pl-10'
                                                    />
                                                </FormControl>
                                                <button className='-translate-y-1/2 absolute left-3 size-5 top-1/2'>
                                                    <Search />
                                                </button>
                                            </FormItem>
                                        )}
                                    />
                                    <div>
                                        <Button onClick={() => handleOpenFilter()} type='button' variant='outline' size='icon' className='size-10'>
                                            <ListFilter className='size-5' />
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </Form>
                    )
            }

            <div className='flex justify-end w-full gap-4 my-4 md:hidden'>
                {renderButtonTable()}
            </div>

            <div className='layout-card md:mt-6'>
                <TableCustomOnlyTwoDept
                    columns={columns}
                    data={commissions}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    headerClassName='justify-start items-center'
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    onRowSelectionChange={setRowSelected}
                    getRowId={row => row.id?.toString()}
                    enableScrollResTable={isOwner}
                    renderActions={(
                        <div className='md:block hidden'>{renderButtonTable()}</div>
                    )}
                    renderTotal={renderTotalComponent}
                />
            </div>

        </>
    );
});
